import { FileDetailsTabs, FileResponseInterface, FileUpload } from 'services/interface';
import { useCallback } from 'react';
import { navigate } from 'gatsby';
import { axios, AxiosConfig, MutationFetcher, SWRKey, useSWRConfig, useSWRImmutable, useSWRMutation } from 'services/swr';
import { useToastNotify } from '@opswat/ui';

const fileUploadKey: SWRKey = { url: '/v4/file' };
const limitsKey: SWRKey = { url: '/v4/apikey/limits/status' };

const getArchivePasswordHeaders = (passwords?: string[]): Record<string, string> => {
    if (!passwords || passwords.length === 0) {
        return {};
    }

    const filteredPasswords = passwords
        .map((password) => password.trim())
        .filter((password) => password !== '');

    if (filteredPasswords.length === 0) {
        return {};
    }

    if (filteredPasswords.length === 1) {
        return { archivepwd: filteredPasswords[0] };
    }

    return filteredPasswords.reduce<Record<string, string>>((headers, password, index) => {
        headers[`archivepwd${index + 1}`] = password;
        return headers;
    }, {});
};

const uploadFile: MutationFetcher<
    FileUpload,
    SWRKey,
    {
        file: File;
        rules?: string;
        sandbox?: boolean;
        privateScan?: boolean;
        archivePasswords?: string[];
        sendArchivePasswords?: boolean;
        onProgress?: (progress: number) => void;
    }
> = async ({ url, ...config }, { arg: { file, rules, sandbox, privateScan, archivePasswords, sendArchivePasswords, onProgress } }) => {
    const data = new FormData();
    data.append('file', file);

    data.append('originalFilename', file.name);

    const hasUnarchiveRule = Boolean(
        rules?.split(',').some((rule) => rule.trim().toLowerCase() === 'unarchive')
    );

    const shouldSendArchivePasswords = Boolean(
        archivePasswords?.some((password) => password.trim() !== '') && (sendArchivePasswords || hasUnarchiveRule)
    );

    const headers = {
        ...config.headers,
        rule: rules,
        ...(sandbox && { sandbox: 'windows10' }),
        ...(privateScan && { privateprocessing: '1' }),
        ...(shouldSendArchivePasswords ? getArchivePasswordHeaders(archivePasswords) : {})
    };

    return axios.post(url, data, {
        ...config,
        headers,
        onUploadProgress: (progressEvent) => {
            if (progressEvent.total && onProgress) {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                onProgress(percentCompleted);
            }
        }
    }).then(res => res.data);
};

const hashLookupKey: SWRKey = { url: '/v4/hash/' };

const hashLookup: MutationFetcher<FileResponseInterface, SWRKey, string> = async ({ url, ...config }, { arg: hash }) => {
    const hashUrl = url + hash;
    return axios.get(hashUrl, config).then(res => res.data);
};

const hashLookupSandboxKey: SWRKey = { url: '/v4/hash/' };

const hashLookupSandbox: MutationFetcher<FileResponseInterface, SWRKey, string> = async ({ url, ...config }, { arg: hash }) => {
    const hashUrl = url + hash + '/sandbox';
    return axios.get(hashUrl, config).then(res => res.data);
};

export const useFileScan = () => {
    const { trigger: upload, isMutating: uploadIsMutating, ...options } = useSWRMutation(fileUploadKey, uploadFile, { revalidate: false });
    const { trigger: lookup, isMutating: lookupIsMutating } = useSWRMutation(hashLookupKey, hashLookup);
    const { trigger: lookupSandbox } = useSWRMutation(hashLookupSandboxKey, hashLookupSandbox);
    const { data: limitsStatus, mutate: refetchLimitsStatus } = useSWRImmutable(limitsKey, { shouldRetryOnError: false });
    const isLoading = lookupIsMutating || uploadIsMutating;
    const { toastNotify } = useToastNotify();

    const { mutate: globalMutate } = useSWRConfig();

    const handleUpload = useCallback(async (
        file: File,
        rules?: string,
        sandbox?: boolean,
        privateScan?: boolean,
        archivePasswords?: string[],
        onProgress?: (progress: number) => void,
        tab?: string,
        sendArchivePasswords?: boolean
    ) => {
        return upload({
            file,
            rules,
            sandbox,
            privateScan,
            archivePasswords,
            sendArchivePasswords,
            onProgress
        })
            .then(async (fileData) => {
                if (fileData?.data_id) {
                    const keys = [`/v4/hash/${fileData?.sha1}/sandbox`, `/v4/hash/${fileData?.sha1}/vulnerability`, `/v4/hash/${fileData?.sha1}/scanhistory`];

                    await refetchLimitsStatus();

                    navigate(`/results/file/${fileData.data_id}/${encodeURIComponent(tab || FileDetailsTabs.Overview)}`, {
                        state: {
                            isSandboxOld: fileData?.sandbox_error ?? false,
                            dataId: fileData?.data_id,
                            limitsStatus,
                            sandboxId: fileData?.last_sandbox_id?.[0]?.sandbox_id || fileData?.sandbox_id,
                        },
                    });

                    globalMutate((key: AxiosConfig) => keys.includes(key?.url), undefined, { revalidate: true });
                }
            })
            .catch((error) => {
                let errorMessage = 'An error occurred while processing the file.';
                if (error?.response?.data?.error?.details?.message) {
                    errorMessage = error.response.data.error.details.message;
                } else if (error?.response?.data?.error?.message) {
                    errorMessage = error.response.data.error.message;
                } else if (error?.messages?.length > 0) {
                    errorMessage = error?.messages?.[0];
                } else if (error?.message) {
                    errorMessage = error.message;
                }
                toastNotify({
                    title: 'Error',
                    message: errorMessage,
                    type: 'error',
                    hasClose: true,
                    duration: 5000,
                });
            });
    }, [upload, refetchLimitsStatus, limitsStatus, globalMutate, toastNotify]);

    const handleScan = useCallback(async (
        file: File,
        rules?: string,
        sandbox?: boolean,
        privateScan?: boolean,
        archivePasswords?: string[],
        onProgress?: (progress: number) => void,
        tab?: string,
        sendArchivePasswords?: boolean
    ) => {
        return handleUpload(file, rules, sandbox, privateScan, archivePasswords, onProgress, tab, sendArchivePasswords);
    }, [handleUpload]);

    return { scan: handleScan, rescan: handleUpload, lookup, lookupSandbox, isLoading, limitsStatus, ...options };
};
